export class Pending {
    constructor() {
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "PENDING"
        });
        Object.defineProperty(this, "isPending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "isSuccess", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isFailure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "val", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    valueOrNull() {
        return null;
    }
    valueOrThrow() {
        throw new Error(`AsyncOp value was accessed but the op is in Pending state.`);
    }
    hasTruthyValue() {
        return false;
    }
    failureValueOrNull() {
        return null;
    }
    failureValueOrThrow() {
        throw new Error(`AsyncOp failure value was accessed but the op is in Pending state.`);
    }
    flatMap() {
        return new Pending();
    }
    mapSuccess() {
        return new Pending();
    }
    mapFailure() {
        return new Pending();
    }
    unpack(_mapS, _mapF, mapP) {
        return mapP();
    }
}
export class Success {
    constructor(value) {
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "SUCCESS"
        });
        Object.defineProperty(this, "isPending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSuccess", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "isFailure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "value", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "val", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.value = value;
        this.val = value;
    }
    valueOrNull() {
        return this.value;
    }
    valueOrThrow() {
        return this.value;
    }
    hasTruthyValue() {
        return !!this.value;
    }
    failureValueOrNull() {
        return null;
    }
    failureValueOrThrow() {
        throw new Error(`AsyncOp failure value was accessed but the op is in Success state.`);
    }
    flatMap(mappingFn) {
        return mappingFn(this.value);
    }
    mapSuccess(mappingFn) {
        return new Success(mappingFn(this.value));
    }
    mapFailure() {
        return new Success(this.value);
    }
    unpack(mapS, _mapF, _mapP) {
        return mapS(this.value);
    }
}
export class Failure {
    constructor(failure) {
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "FAILURE"
        });
        Object.defineProperty(this, "isPending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSuccess", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isFailure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "failure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "val", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.failure = failure;
        this.val = failure;
    }
    valueOrNull() {
        return null;
    }
    valueOrThrow() {
        throw new Error(`AsyncOp value was accessed but the op is in Failure state.`);
    }
    hasTruthyValue() {
        return false;
    }
    failureValueOrNull() {
        return this.failure;
    }
    failureValueOrThrow() {
        return this.failure;
    }
    flatMap() {
        return new Failure(this.failure);
    }
    mapSuccess() {
        return new Failure(this.failure);
    }
    mapFailure(mappingFn) {
        return new Failure(mappingFn(this.failure));
    }
    unpack(_mapS, mapF, _mapP) {
        return mapF(this.failure);
    }
}
/**
 * Folds multiple ops into a single op.
 *
 * To return a Success, all ops provided must be a Success. If any Failures are
 * encountered, will return the first one found.
 *
 * If neither of these conditions is true, will return Pending.
 *
 * Note that if passed an empty array, will always return a Success (with an
 * empty array as value). This mimics the semantics of many JS constructs, like
 * Promise.all or Array.prototype.every.
 */
export function fold(ops) {
    // Note that due to the semantics of `every`, if the array provided to `fold`
    // is empty, the result will be a Success with an empty array.
    if (ops.every((v) => v.isSuccess)) {
        return new Success(ops.map((op) => op.value));
    }
    const firstFail = ops.find((op) => op.isFailure);
    if (firstFail) {
        return firstFail;
    }
    return new Pending();
}
